import { useState } from "react";
import {
    TOOTH_GRID,
    UNIVERSAL_TO_FDI,
    SYSTEM_OPTIONS,
    getStoredToothSystem,
    setStoredToothSystem,
} from "@/lib/toothSystem";

/**
 * Shared tooth picker — used by the public Send-a-case form, the Admin
 * manual case-entry modal, and any future place a clinician needs to
 * indicate teeth.
 *
 * Stores numbers as Universal (1-32) so historic data and the CSV importer
 * keep working unchanged. The picker DISPLAYS them as FDI by default —
 * controlled via the system dropdown in the header. The selection persists
 * in localStorage per browser so the user only picks their system once.
 *
 * Layout matches the user's reference screenshot: chart-left = patient's
 * right (FDI quadrants 1 + 4), chart-right = patient's left (quadrants 2 + 3),
 * with the inner-most teeth (centrals) meeting at the midline.
 */
export default function ToothChart({ selected, onChange, defaultSystem = null }) {
    const [system, setSystem] = useState(defaultSystem || getStoredToothSystem());

    const switchSystem = (next) => {
        setSystem(next);
        setStoredToothSystem(next);
    };

    const toggle = (universal) => {
        if (selected.includes(universal)) {
            onChange(selected.filter((x) => x !== universal));
        } else {
            onChange([...selected, universal].sort((a, b) => a - b));
        }
    };

    const label = (cell) => (system === "fdi" ? cell.fdi : cell.universal);

    return (
        <div data-testid="tooth-chart" className="space-y-3">
            {/* System toggle */}
            <div className="flex items-center justify-between gap-3">
                <div className="text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">
                    Tooth numbering
                </div>
                <select
                    data-testid="tooth-system-select"
                    value={system}
                    onChange={(e) => switchSystem(e.target.value)}
                    className="px-2.5 py-1 rounded-md border border-slate-200 text-[12px] bg-white focus:border-cyan-500 outline-none"
                >
                    {SYSTEM_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            </div>

            {/* Quadrant labels — upper */}
            <div className="flex items-center justify-between text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500 pt-1">
                <span data-testid="tooth-quadrant-upper-left">Upper Left · I</span>
                <span data-testid="tooth-quadrant-upper-right">Upper Right · II</span>
            </div>

            {/* Upper row: chart-left (quadrant 1, teeth go highest-FDI → midline) + chart-right (quadrant 2, midline → highest-FDI) */}
            <div className="grid grid-cols-[1fr_8px_1fr] gap-1.5 items-center">
                <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(8, minmax(0,1fr))" }}>
                    {TOOTH_GRID.upper.left.map((cell) => (
                        <ToothCell
                            key={`u-l-${cell.universal}`}
                            cell={cell}
                            label={label(cell)}
                            selected={selected.includes(cell.universal)}
                            onClick={() => toggle(cell.universal)}
                        />
                    ))}
                </div>
                <div className="h-9 border-l border-slate-300" aria-hidden="true" />
                <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(8, minmax(0,1fr))" }}>
                    {TOOTH_GRID.upper.right.map((cell) => (
                        <ToothCell
                            key={`u-r-${cell.universal}`}
                            cell={cell}
                            label={label(cell)}
                            selected={selected.includes(cell.universal)}
                            onClick={() => toggle(cell.universal)}
                        />
                    ))}
                </div>
            </div>

            {/* Mid-line divider */}
            <div className="border-t border-dashed border-slate-200" />

            {/* Lower row: same layout, mirrored vertically (quadrant 4 left, quadrant 3 right) */}
            <div className="grid grid-cols-[1fr_8px_1fr] gap-1.5 items-center">
                <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(8, minmax(0,1fr))" }}>
                    {TOOTH_GRID.lower.left.map((cell) => (
                        <ToothCell
                            key={`l-l-${cell.universal}`}
                            cell={cell}
                            label={label(cell)}
                            selected={selected.includes(cell.universal)}
                            onClick={() => toggle(cell.universal)}
                        />
                    ))}
                </div>
                <div className="h-9 border-l border-slate-300" aria-hidden="true" />
                <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(8, minmax(0,1fr))" }}>
                    {TOOTH_GRID.lower.right.map((cell) => (
                        <ToothCell
                            key={`l-r-${cell.universal}`}
                            cell={cell}
                            label={label(cell)}
                            selected={selected.includes(cell.universal)}
                            onClick={() => toggle(cell.universal)}
                        />
                    ))}
                </div>
            </div>

            {/* Quadrant labels — lower */}
            <div className="flex items-center justify-between text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500">
                <span data-testid="tooth-quadrant-lower-left">Lower Left · IV</span>
                <span data-testid="tooth-quadrant-lower-right">Lower Right · III</span>
            </div>

            <div className="flex items-center justify-between text-[10.5px] font-mono-tr tracking-[0.2em] uppercase text-slate-500 pt-1">
                <span>{selected.length} selected</span>
                <span>
                    {selected.length > 0 && (
                        <span data-testid="tooth-chart-display">
                            {selected.map((u) => (system === "fdi" ? UNIVERSAL_TO_FDI[u] : u)).join(", ")}
                        </span>
                    )}
                </span>
            </div>
        </div>
    );
}

function ToothCell({ cell, label, selected, onClick }) {
    return (
        <button
            type="button"
            data-testid={`tooth-${cell.universal}`}
            data-fdi={cell.fdi}
            onClick={onClick}
            className={`tooth-cell ${selected ? "selected" : ""}`}
            title={`Universal ${cell.universal} · FDI ${cell.fdi}`}
        >
            {label}
        </button>
    );
}
